import React from 'react';
import c from 'classnames';

import { CheckMark, Minus } from '@lyearn/icons';

import Stack from '../Stack';
import { checkBoxFocusStyles, checkBoxStyles } from './utils/styles';
import BaseCheckbox from './BaseCheckbox';
import { CheckboxProps } from './types';
import { CheckboxSize } from './types';
import {
  CHECK_ICON_SIZE_CLASSES,
  CHECK_SIZE_CLASSES,
  INDETERMINATE_ICON_SIZE_CLASSES,
  SIZE_BORDER_RADIUS_CLASSES,
} from './utils';

const getIcon = (size: CheckboxSize) => {
  const sizeClasses = CHECK_SIZE_CLASSES[size];
  const borderRadius = SIZE_BORDER_RADIUS_CLASSES[size];

  return (
    <Stack
      className={c(
        sizeClasses,
        borderRadius,
        'border border-solid border-border-medium group-hover:border-border-dark',
      )}
    />
  );
};

const getCheckedIcon = (isParent: boolean, size: CheckboxSize) => {
  const sizeClasses = CHECK_SIZE_CLASSES[size];
  const iconClasses = CHECK_ICON_SIZE_CLASSES[size];
  const borderRadius = SIZE_BORDER_RADIUS_CLASSES[size];

  if (isParent) {
    return (
      <Stack
        alignItems="center"
        className={c(
          sizeClasses,
          borderRadius,
          'border border-solid border-interactive-secondary-hover group-hover:border-interactive-secondary-active ly-checkbox',
        )}
        justifyContent="center">
        <CheckMark className={iconClasses} fillColorToken="icon-active" />
      </Stack>
    );
  }

  return (
    <Stack
      alignItems="center"
      className={c(
        sizeClasses,
        borderRadius,
        'border bg-interactive-primary group-hover:bg-interactive-primary-hover',
      )}
      justifyContent="center">
      <CheckMark className={iconClasses} fillColorToken="icon-white" />
    </Stack>
  );
};

const getIndeterminateIcon = (size: CheckboxSize) => {
  const sizeClasses = CHECK_SIZE_CLASSES[size];
  const iconClasses = INDETERMINATE_ICON_SIZE_CLASSES[size];
  const borderRadius = SIZE_BORDER_RADIUS_CLASSES[size];

  return (
    <Stack
      alignItems="center"
      className={c(
        sizeClasses,
        borderRadius,
        'border border-solid border-interactive-secondary-hover group-hover:border-interactive-secondary-active ly-checkbox',
      )}
      justifyContent="center">
      <Minus className={iconClasses} fillColorToken="icon-active" />
    </Stack>
  );
};

const DefaultCheckbox = React.forwardRef<
  HTMLButtonElement,
  CheckboxProps & {
    parent?: boolean;
    size?: CheckboxSize;
  }
>((props, ref) => {
  const { className, parent = false, size = 'medium', ...rest } = props;

  const icon = getIcon(size);
  const checkedIcon = getCheckedIcon(parent, size);
  const indeterminateIcon = getIndeterminateIcon(size);

  return (
    <BaseCheckbox
      ref={ref}
      disableRipple
      checkedIcon={checkedIcon}
      className={c('p-0 group', className, checkBoxStyles, checkBoxFocusStyles)}
      icon={icon}
      indeterminateIcon={indeterminateIcon}
      {...rest}
    />
  );
});

export default React.memo(DefaultCheckbox);
